import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Globe, MapPin, Pencil, Phone, Plus } from "lucide-react";
import type { GasStation, GasStationBrand } from "@/types/database";
import { AddGasStationModal } from "@/components/gas-stations/AddGasStationModal";
import { EditGasStationBrandModal } from "@/components/gas-stations/EditGasStationBrandModal";
import { GasStationCard } from "@/components/gas-stations/GasStationCard";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { SafeImage } from "@/components/ui/SafeImage";
import { Workspace, WorkspaceSection } from "@/components/ui/Workspace";
import { useDeleteGasStationBrand, useGasStations } from "@/lib/queries";
import { formatDate, formatNumber, imageSrc } from "@/lib/utils";

function BrandLogo({ brand, size = "md" }: { brand: GasStationBrand; size?: "md" | "lg" }) {
  const logoSrc = imageSrc(brand.brandLogoUrl);
  const name = brand.brandName ?? "Brand";
  const dimensions = size === "lg" ? "h-20 w-20 text-lg" : "h-12 w-12 text-sm";

  if (logoSrc) {
    return (
      <SafeImage
        src={logoSrc}
        alt={name}
        width={size === "lg" ? 80 : 48}
        height={size === "lg" ? 80 : 48}
        className={`${dimensions} object-contain`}
      />
    );
  }

  return (
    <div
      className={`flex shrink-0 items-center justify-center rounded-2xl bg-accent-subtle font-bold text-accent-foreground ${dimensions}`}
    >
      {name.slice(0, 2).toUpperCase()}
    </div>
  );
}

function stationsForBrand(stations: GasStation[], brandId: string): GasStation[] {
  return stations.filter((station) => station.brandID === brandId);
}

export function GasStationBrandDetailPanel({ brand }: { brand: GasStationBrand }) {
  const navigate = useNavigate();
  const deleteBrand = useDeleteGasStationBrand();
  const { data: stations = [], isLoading } = useGasStations();
  const [editOpen, setEditOpen] = useState(false);
  const [addStationOpen, setAddStationOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleting = deleteBrand.isPending;
  const name = brand.brandName ?? "Unnamed brand";
  const phone = brand.phone != null ? String(brand.phone) : null;

  const brandStations = useMemo(
    () => stationsForBrand(stations, brand.id),
    [stations, brand.id]
  );

  async function handleDelete() {
    setDeleteError(null);
    try {
      await deleteBrand.mutateAsync(brand.id);
      setDeleteOpen(false);
      navigate("/gas-stations");
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : "Failed to delete brand");
    }
  }

  return (
    <>
      <Workspace>
        <WorkspaceSection className="grid gap-0 lg:grid-cols-[1fr_1.1fr]">
          <div className="border-b border-border p-4 sm:p-5 lg:border-b-0 lg:border-r">
            <div className="flex items-start justify-between gap-4">
              <BrandLogo brand={brand} size="lg" />
              <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)}>
                <Pencil className="h-4 w-4" />
                Edit brand
              </Button>
            </div>
            <h1 className="mt-4 text-2xl font-bold tracking-tight sm:text-3xl">{name}</h1>

          <div className="mt-3 flex flex-wrap gap-1.5">
            {brand.country ? (
              <span className="stat-pill">
                <MapPin className="h-3.5 w-3.5" />
                {brand.country}
              </span>
            ) : null}
            {brand.website ? (
              <a
                href={brand.website}
                target="_blank"
                rel="noreferrer"
                className="stat-pill transition hover:bg-canvas-hover"
              >
                <Globe className="h-3.5 w-3.5" />
                Website
              </a>
            ) : null}
            {phone ? (
              <span className="stat-pill">
                <Phone className="h-3.5 w-3.5" />
                {phone}
              </span>
            ) : null}
          </div>

          {brand.description ? (
            <p className="mt-6 text-sm leading-relaxed text-muted">{brand.description}</p>
          ) : (
            <p className="mt-6 text-sm text-muted">No description provided.</p>
          )}

          <p className="mt-6 text-xs text-muted">
            Added {formatDate(brand.created_at)}
          </p>

          <Button
            variant="danger"
            className="mt-6 w-full sm:w-auto"
            onClick={() => {
              setDeleteError(null);
              setDeleteOpen(true);
            }}
            disabled={deleting}
          >
            Delete brand
          </Button>
        </div>

        <div className="flex min-h-[220px] items-center justify-center bg-[#fafafa] p-8 lg:min-h-[280px]">
          {imageSrc(brand.brandLogoUrl) ? (
            <SafeImage
              src={brand.brandLogoUrl}
              alt={name}
              width={240}
              height={240}
              className="max-h-48 max-w-full object-contain"
            />
          ) : (
            <span className="text-4xl font-bold text-[#d2d2d7]">{name.slice(0, 2).toUpperCase()}</span>
          )}
        </div>
      </WorkspaceSection>

      <WorkspaceSection border className="p-4 sm:p-5">
        <div className="mb-5 flex items-end justify-between gap-4">
          <div>
            <h2 className="font-bold">Network stations</h2>
            <p className="mt-1 text-sm text-muted">
              Fuel stations linked to this brand via brandID.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {!isLoading ? (
              <p className="text-sm text-muted">
                <span className="font-bold text-foreground">{formatNumber(brandStations.length)}</span> stations
              </p>
            ) : null}
            <Button variant="brand" size="sm" onClick={() => setAddStationOpen(true)}>
              <Plus className="h-4 w-4" />
              Add station
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="h-40 animate-pulse rounded-3xl bg-border/40" />
            ))}
          </div>
        ) : brandStations.length === 0 ? (
          <div className="rounded-2xl bg-canvas px-6 py-12 text-center">
            <p className="font-bold text-foreground">No matching stations</p>
            <p className="mt-1 text-sm text-muted">
              No fuel stations are linked to <span className="font-semibold text-foreground">{name}</span> yet.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
            {brandStations.map((station) => (
              <GasStationCard key={station.id} station={station} />
            ))}
          </div>
        )}
      </WorkspaceSection>
    </Workspace>

      <EditGasStationBrandModal
        brand={brand}
        open={editOpen}
        onClose={() => setEditOpen(false)}
      />

      <AddGasStationModal
        open={addStationOpen}
        onClose={() => setAddStationOpen(false)}
        defaultBrandID={brand.id}
      />

      <Modal
        open={deleteOpen}
        onClose={() => {
          if (deleting) return;
          setDeleteOpen(false);
          setDeleteError(null);
        }}
        title="Delete brand"
        className="max-w-lg"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setDeleteOpen(false);
                setDeleteError(null);
              }}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Deleting…" : "Delete brand"}
            </Button>
          </>
        }
      >
        {deleteError ? (
          <p className="mb-4 rounded-2xl bg-red-50 px-4 py-2.5 text-sm text-red-600">{deleteError}</p>
        ) : null}
        <p className="text-foreground">
          Are you sure you want to delete <span className="font-bold">{name}</span>? This removes the brand catalogue
          entry only — linked fuel stations will not be deleted.
        </p>
      </Modal>
    </>
  );
}
